// /frontend/src/pages/AdminUsers.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    CircularProgress,
    Modal,
    Button,
    Chip,
    Link as MuiLink
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import GenericForm from '../components/GenericForm.js';
import { userFormFields } from '../forms/userFormFields.js';
import * as courseService from '../services/courseService';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({});
  const [allCategories, setAllCategories] = useState([]);
  const [allCourses, setAllCourses] = useState([]);

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%', // Reso più responsive
    maxWidth: 600,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    maxHeight: '90vh',
    overflowY: 'auto'
  };

  const fetchAllData = () => {
    setLoading(true);
    Promise.all([
      axios.get('/api/admin/users'),
      axios.get('/api/categories'),
      courseService.getAllCourses()
    ]).then(([usersRes, categoriesRes, coursesRes]) => {
      setUsers(usersRes.data);
      setAllCategories(categoriesRes.data);
      setAllCourses(coursesRes);
    }).catch(err => console.error("Errore nel caricare i dati:", err))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchAllData(); }, []);

  const handleDelete = (userId) => {
    if (window.confirm('Sei sicuro di voler eliminare questo utente?')) {
      axios.delete(`/api/admin/users/${userId}`).then(() => fetchAllData());
    }
  };

  const handleViewDetails = (userId) => {
    axios.get(`/api/admin/user-details/${userId}`).then(res => setSelectedUser(res.data));
  };
  
  const handleOpenEditModal = (userToEdit) => {
    axios.get(`/api/admin/user-details/${userToEdit.id}`).then(res => {
      const userData = res.data;
      const userCategories = userData.categories.map(c => c.category);
      const userCourses = userData.courses ? userData.courses.map(c => c.course) : [];
      setFormData({ ...userData, categories: userCategories, courses: userCourses });
      setEditingUser(userData);
    });
  };

  const handleCloseEditModal = () => {
    setEditingUser(null);
    setFormData({});
  };
  
  const handleUpdate = (e) => {
    e.preventDefault();
    const dataToSend = { ...formData };
    if (dataToSend.categories && Array.isArray(dataToSend.categories)) {
      dataToSend.categories = dataToSend.categories.map(cat => cat.id);
    }
    if (dataToSend.courses && Array.isArray(dataToSend.courses)) {
      dataToSend.courses = dataToSend.courses.map(course => course.id);
    }
    axios.put(`/api/admin/users/${editingUser.id}`, dataToSend)
      .then(() => {
        handleCloseEditModal();
        fetchAllData();
      })
      .catch(err => alert('Errore durante l\'aggiornamento.'));
  };

  const adminUserFields = userFormFields.map(field => {
    if (field.name === 'categories') {
      return { ...field, options: allCategories };
    }
    if (field.name === 'courses') {
      return { ...field, options: allCourses };
    }
    return field;
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Gestione Utenti Alumni</Typography>
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 720 }}>
          <TableHead>
            <TableRow>
              <TableCell>Nome Completo</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Stato Approvazione</TableCell>
              {/* --- MODIFICA 1: Aggiunta delle nuove intestazioni --- */}
              <TableCell>Stato Disponibilità</TableCell>
              <TableCell>Ultima Attività</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.fullName}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip 
                    label={user.status} 
                    color={user.status === 'approved' ? 'success' : user.status === 'pending' ? 'warning' : 'default'}
                    size="small"
                  />
                </TableCell>
                {/* --- MODIFICA 2: Aggiunta delle nuove celle con i dati --- */}
                <TableCell>
                  <Chip 
                    label={user.availabilityStatus} 
                    color={user.availabilityStatus === 'attivo' ? 'success' : 'default'}
                    variant="outlined"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleDateString('it-IT') : 'N/D'}
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleViewDetails(user.id)} title="Vedi Dettagli"><VisibilityIcon /></IconButton>
                  <IconButton onClick={() => handleOpenEditModal(user)} color="primary" title="Modifica"><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDelete(user.id)} color="error" title="Elimina"><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Modal open={!!selectedUser} onClose={() => setSelectedUser(null)}>
        <Box sx={style}>
          {selectedUser && <>
            <Typography variant="h6">Dettagli Utente</Typography>
            <Typography sx={{ mt: 2 }}><b>Nome:</b> {selectedUser.fullName}</Typography>
            <Typography><b>Email:</b> {selectedUser.email}</Typography>
            <Typography><b>Telefono:</b> {selectedUser.phone || 'N/D'}</Typography>
            <Typography><b>Indirizzo:</b> {selectedUser.address || 'N/D'}</Typography>
            <Typography>
                <b>Curriculum Vitae:</b> 
                {selectedUser.resume ? <MuiLink href={`/${selectedUser.resume}`} target="_blank" rel="noopener noreferrer">Scarica CV</MuiLink> : 'Non caricato'}
            </Typography>
            <Typography><b>Competenze:</b> {selectedUser.categories.map(c => c.category.name).join(', ')}</Typography>
          </>}
        </Box>
      </Modal>

      <Modal open={!!editingUser} onClose={handleCloseEditModal}>
        <Box sx={style}>
          <Typography variant="h6">Modifica Utente</Typography>
          <Box component="form" onSubmit={handleUpdate} sx={{ mt: 2 }}>
            <GenericForm
              fields={adminUserFields}
              formData={formData}
              setFormData={setFormData}
            />
            <Button type="submit" variant="contained" sx={{ mt: 3 }}>Salva Modifiche</Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default AdminUsers;